import { dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import { useBrands } from '@api/catalog/brand';
import { getBrands } from '@api/catalog/brand/api';
import { getPagination } from '@api/common/helpers';
import { getSeoTags } from '@api/seo/api';

import PageSection from '@components/PageSection';
import PageWrapper from '@components/PageWrapper';
import { Breadcrumbs } from '@components/uiKit/Breadcrumbs';

import { useResize } from '@scripts/hooks/useResize';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { ISSRPageProps } from '@scripts/server/types';

import BrandsCarousel from '../catalog/[category]/components/BrandsCarousel';
import BrandList from './components/BrandList';
import LetterList from './components/LetterList';
import { FilterTrigger } from './consts';
import { filterOfBrands } from './helper';
import styles from './index.module.scss';

const Brands = () => {
    const { isScreenMd } = useResize();
    const { query } = useRouter();
    const { data: brands } = useBrands({
        pagination: getPagination(-1),
        filter: {
            is_active: true,
        },
    });

    const filterLetter = useMemo(() => query.filter as string, [query.filter]);

    const allBrands = useMemo(
        () => (brands ? filterOfBrands(brands.data, FilterTrigger.Default) : null),
        [brands?.data]
    );

    useEffect(() => {
        const yOffset = isScreenMd ? -250 : -110;
        const element = document.getElementById(filterLetter);
        if (element) {
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }, [filterLetter, isScreenMd]);

    const breadcrumbsBrandstems = useMemo(
        () => [
            {
                label: 'Главная',
                href: '/',
            },
            {
                label: 'Бренды',
                href: '/brands/',
            },
        ],
        []
    );

    return (
        <PageWrapper>
            <PageSection className={styles.page} containerClassName={styles.pageContainer}>
                <div className={styles.stack}>
                    <div className={styles.breadcrumbs}>
                        <Breadcrumbs items={breadcrumbsBrandstems} withPageOffset />
                    </div>

                    <div>{allBrands && <LetterList brands={allBrands} />}</div>

                    <div className={styles.carousel}>
                        <BrandsCarousel />
                    </div>

                    {allBrands ? <BrandList brands={allBrands} /> : 'Бренды не найдены'}
                </div>
            </PageSection>
        </PageWrapper>
    );
};

export default Brands;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ISSRPageProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        try {
            const { key: brandsKey, fetch: brandsFetch } = getBrands(apiClient)({
                pagination: getPagination(-1),
                filter: {
                    is_active: true,
                },
            });
            const { key: brandsPopularKey, fetch: brandsPopularFetch } = getBrands(apiClient)({
                filter: { is_popular: true },
            });
            const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)({
                filter: {
                    url: '/brands',
                    type: 'other',
                },
            });

            await Promise.all([
                queryClient.prefetchQuery({ queryKey: brandsKey, queryFn: brandsFetch }),
                queryClient.prefetchQuery({ queryKey: brandsPopularKey, queryFn: brandsPopularFetch }),
                queryClient.prefetchQuery({ queryKey: seoTagsKey, queryFn: baseSeoTagsFetch }),
            ]);

            return {
                props: {
                    dehydratedState: dehydrate(queryClient),
                    ...props,
                },
            };
        } catch (error) {
            return {
                ...props,
                isNotFound: true,
            };
        }
    });
